// Demo Authentication Service - Simula comportamiento real sin APIs

// Estructura de datos simulada
const DEMO_DATA_KEY = 'numerologia_demo_data';

// Inicializar datos demo
const initDemoData = () => {
  const existing = localStorage.getItem(DEMO_DATA_KEY);
  if (!existing) {
    const demoData = {
      accounts: [],
      users: [],
      memberships: [],
      invitations: [],
      profiles: [],
      subscriptions: []
    };
    localStorage.setItem(DEMO_DATA_KEY, JSON.stringify(demoData));
    return demoData;
  }
  return JSON.parse(existing);
};

// Obtener datos demo
const getDemoData = () => {
  return JSON.parse(localStorage.getItem(DEMO_DATA_KEY) || '{}');
};

// Guardar datos demo
const saveDemoData = (data) => {
  localStorage.setItem(DEMO_DATA_KEY, JSON.stringify(data));
};

// Generar ID único
const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

// Generar token de invitación
const generateInvitationToken = () => 'inv_' + Math.random().toString(36).substr(2, 20);

// Planes disponibles
export const PLANS = {
  solo: {
    id: 'solo',
    name: 'Solo',
    description: 'Perfecto para uso personal',
    price: 299,
    seats: 1,
    features: ['Numerología personal', 'Calendario interactivo', 'Etapas de vida', 'Soporte básico']
  },
  duo: {
    id: 'duo',
    name: 'Duo',
    description: 'Para parejas y relaciones',
    price: 499,
    seats: 2,
    features: ['Todo lo de Solo', '2 perfiles independientes', 'Compatibilidad numerológica', 'Soporte prioritario']
  },
  familiar: {
    id: 'familiar',
    name: 'Familiar',
    description: 'Para toda la familia',
    price: 799,
    seats: 5,
    features: ['Todo lo de Duo', 'Hasta 5 perfiles', 'Análisis familiar', 'Gestión de invitaciones', 'Soporte premium']
  }
};

// Clase principal del servicio de autenticación demo
class DemoAuthService {
  constructor() {
    this.currentUser = null;
    this.initData();
    this.loadCurrentUser();
  }

  initData() {
    initDemoData();
  }

  loadCurrentUser() {
    const userId = localStorage.getItem('current_user_id');
    if (userId) {
      const data = getDemoData();
      this.currentUser = data.users.find(u => u.id === userId);
    }
  }

  // Simular compra de plan
  async purchasePlan(planId, userInfo) {
    const plan = PLANS[planId];
    if (!plan) throw new Error('Plan no encontrado');

    // Simular delay de pago
    await new Promise(resolve => setTimeout(resolve, 2000));

    const data = getDemoData();
    
    // Crear usuario
    const userId = generateId();
    const user = {
      id: userId,
      email: userInfo.email,
      password_hash: 'demo_hash', // En producción sería real
      onboarding_done: false,
      created_at: new Date().toISOString()
    };

    // Crear cuenta
    const accountId = generateId();
    const account = {
      id: accountId,
      owner_user_id: userId,
      stripe_customer_id: 'cus_demo_' + accountId,
      created_at: new Date().toISOString()
    };

    // Crear suscripción
    const subscriptionId = generateId();
    const subscription = {
      id: subscriptionId,
      account_id: accountId,
      stripe_subscription_id: 'sub_demo_' + subscriptionId,
      plan_id: planId,
      status: 'active',
      seats_quantity: plan.seats,
      seats_used: 1, // El comprador usa 1 asiento
      price: plan.price,
      created_at: new Date().toISOString()
    };

    // Crear membership para el owner
    const membership = {
      user_id: userId,
      account_id: accountId,
      role: 'owner',
      created_at: new Date().toISOString()
    };

    // Guardar todo
    data.users.push(user);
    data.accounts.push(account);
    data.subscriptions.push(subscription);
    data.memberships.push(membership);
    
    saveDemoData(data);

    // Auto-login
    this.currentUser = user;
    localStorage.setItem('current_user_id', userId);

    return {
      user,
      account,
      subscription,
      success: true,
      message: '¡Compra exitosa! Bienvenido a Numerología Cotidiana.'
    };
  }

  // Login simulado
  async login(email, password) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const data = getDemoData();
    const user = data.users.find(u => u.email === email);
    
    if (!user) {
      throw new Error('Usuario no encontrado');
    }

    // En demo, cualquier password funciona
    this.currentUser = user;
    localStorage.setItem('current_user_id', user.id);
    
    return { user, success: true };
  }

  // Logout
  logout() {
    this.currentUser = null;
    localStorage.removeItem('current_user_id');
  }

  // Obtener usuario actual
  getCurrentUser() {
    return this.currentUser;
  }

  // Obtener cuenta del usuario actual
  getCurrentAccount() {
    if (!this.currentUser) return null;
    
    const data = getDemoData();
    const membership = data.memberships.find(m => m.user_id === this.currentUser.id);
    if (!membership) return null;
    
    return data.accounts.find(a => a.id === membership.account_id);
  }

  // Obtener suscripción actual
  getCurrentSubscription() {
    const account = this.getCurrentAccount();
    if (!account) return null;
    
    const data = getDemoData();
    return data.subscriptions.find(s => s.account_id === account.id);
  }

  // Invitar usuario
  async inviteUser(email) {
    const account = this.getCurrentAccount();
    const subscription = this.getCurrentSubscription();
    
    if (!account || !subscription) {
      throw new Error('No tienes permisos para invitar');
    }

    if (subscription.seats_used >= subscription.seats_quantity) {
      throw new Error('No tienes asientos disponibles');
    }

    const data = getDemoData();
    
    // Verificar si ya está invitado
    const existingInvitation = data.invitations.find(
      inv => inv.email === email && inv.account_id === account.id && inv.status === 'pending'
    );
    
    if (existingInvitation) {
      throw new Error('Ya existe una invitación pendiente para este email');
    }

    // Crear invitación
    const invitation = {
      id: generateId(),
      account_id: account.id,
      email: email,
      token: generateInvitationToken(),
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 días
      status: 'pending',
      created_at: new Date().toISOString()
    };

    data.invitations.push(invitation);
    saveDemoData(data);

    // Simular envío de email (en producción sería real)
    console.log(`📧 Invitación enviada a ${email} con token: ${invitation.token}`);

    return {
      invitation,
      inviteLink: `${window.location.origin}/invite/${invitation.token}`,
      success: true,
      message: `Invitación enviada a ${email}`
    };
  }

  // Aceptar invitación
  async acceptInvitation(token, userInfo) {
    const data = getDemoData();
    const invitation = data.invitations.find(inv => inv.token === token && inv.status === 'pending');
    
    if (!invitation) {
      throw new Error('Invitación no válida o expirada');
    }

    if (new Date() > new Date(invitation.expires_at)) {
      throw new Error('La invitación ha expirado');
    }

    // Crear usuario
    const userId = generateId();
    const user = {
      id: userId,
      email: invitation.email,
      password_hash: 'demo_hash',
      onboarding_done: false,
      created_at: new Date().toISOString()
    };

    // Crear membership
    const membership = {
      user_id: userId,
      account_id: invitation.account_id,
      role: 'member',
      created_at: new Date().toISOString()
    };

    // Actualizar suscripción (incrementar asientos usados)
    const subscription = data.subscriptions.find(s => s.account_id === invitation.account_id);
    if (subscription) {
      subscription.seats_used += 1;
    }

    // Marcar invitación como aceptada
    invitation.status = 'accepted';
    invitation.accepted_at = new Date().toISOString();

    // Guardar cambios
    data.users.push(user);
    data.memberships.push(membership);
    saveDemoData(data);

    // Auto-login del nuevo usuario
    this.currentUser = user;
    localStorage.setItem('current_user_id', userId);

    return {
      user,
      success: true,
      message: '¡Bienvenido! Completa tu perfil para comenzar.'
    };
  }

  // Obtener invitaciones de la cuenta
  getAccountInvitations() {
    const account = this.getCurrentAccount();
    if (!account) return [];

    const data = getDemoData();
    return data.invitations.filter(inv => inv.account_id === account.id);
  }

  // Obtener miembros de la cuenta
  getAccountMembers() {
    const account = this.getCurrentAccount();
    if (!account) return [];

    const data = getDemoData();
    const memberships = data.memberships.filter(m => m.account_id === account.id);
    
    return memberships.map(membership => {
      const user = data.users.find(u => u.id === membership.user_id);
      const profile = data.profiles.find(p => p.user_id === membership.user_id);
      
      return {
        ...membership,
        user,
        profile,
        isCurrentUser: user?.id === this.currentUser?.id
      };
    });
  }

  // Guardar perfil
  async saveProfile(profileData) {
    if (!this.currentUser) throw new Error('No hay usuario logueado');

    const data = getDemoData();
    
    // Buscar perfil existente
    const existingProfileIndex = data.profiles.findIndex(p => p.user_id === this.currentUser.id);
    
    const profile = {
      user_id: this.currentUser.id,
      ...profileData,
      updated_at: new Date().toISOString()
    };

    if (existingProfileIndex >= 0) {
      data.profiles[existingProfileIndex] = profile;
    } else {
      profile.id = generateId();
      profile.created_at = new Date().toISOString();
      data.profiles.push(profile);
    }

    // Marcar onboarding como completado
    const userIndex = data.users.findIndex(u => u.id === this.currentUser.id);
    if (userIndex >= 0) {
      data.users[userIndex].onboarding_done = true;
      this.currentUser.onboarding_done = true;
    }

    saveDemoData(data);
    return { profile, success: true };
  }

  // Obtener perfil del usuario actual
  getCurrentProfile() {
    if (!this.currentUser) return null;
    
    const data = getDemoData();
    return data.profiles.find(p => p.user_id === this.currentUser.id);
  }

  // Simular agregar asiento
  async addSeat() {
    const subscription = this.getCurrentSubscription();
    if (!subscription) throw new Error('No hay suscripción activa');

    // Simular delay de Stripe
    await new Promise(resolve => setTimeout(resolve, 1500));

    const data = getDemoData();
    const subIndex = data.subscriptions.findIndex(s => s.id === subscription.id);
    
    if (subIndex >= 0) {
      data.subscriptions[subIndex].seats_quantity += 1;
      // Simular costo adicional
      data.subscriptions[subIndex].price += 100; // $100 MXN por asiento adicional
      saveDemoData(data);
    }

    return { success: true, message: 'Asiento agregado exitosamente' };
  }

  // Remover usuario (desactivar asiento)
  async removeUser(userId) {
    const account = this.getCurrentAccount();
    if (!account || account.owner_user_id !== this.currentUser.id) {
      throw new Error('No tienes permisos para remover usuarios');
    }

    const data = getDemoData();
    
    // Remover membership
    data.memberships = data.memberships.filter(m => m.user_id !== userId);
    
    // Actualizar asientos usados
    const subscription = data.subscriptions.find(s => s.account_id === account.id);
    if (subscription && subscription.seats_used > 1) {
      subscription.seats_used -= 1;
    }

    saveDemoData(data);
    return { success: true, message: 'Usuario removido exitosamente' };
  }

  // Limpiar datos demo (para testing)
  clearDemoData() {
    localStorage.removeItem(DEMO_DATA_KEY);
    localStorage.removeItem('current_user_id');
    this.currentUser = null;
    this.initData();
  }
}

// Instancia singleton
export const demoAuth = new DemoAuthService();

// Funciones de utilidad
export const isLoggedIn = () => demoAuth.getCurrentUser() !== null;
export const getCurrentUser = () => demoAuth.getCurrentUser();
export const getCurrentAccount = () => demoAuth.getCurrentAccount();
export const getCurrentSubscription = () => demoAuth.getCurrentSubscription();
